import { useQuery } from '@tanstack/react-query';
import type { AmbitoResponse } from '@sime/shared';
import { apiClient } from '@/api/client';

interface AmbitosApiResponse {
  success: true;
  data: AmbitoResponse[];
}

/** Query key for ambitos (used for cache invalidation) */
export const ambitoKeys = {
  all: ['ambitos'] as const,
};

/**
 * Fetches the list of ambitos from the backend.
 * Replaces the hardcoded AMBITOS constant.
 * Cached for 5 minutes since ambitos change very rarely.
 */
export function useAmbitos() {
  return useQuery({
    queryKey: ambitoKeys.all,
    queryFn: async () => {
      const response = await apiClient<AmbitosApiResponse>('/ambitos');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hardcoded fallback for when the API is not available — used during loading
 * to prevent layout jumps. Derived from `AMBITO_CANONICAL` (single source of
 * truth) so it cannot drift from the seed/import scripts again.
 */
import { AMBITO_CANONICAL } from '@sime/shared';

export const AMBITOS_FALLBACK: AmbitoResponse[] = AMBITO_CANONICAL.map((a) => ({
  id: a.id,
  nombre: a.nombre,
  codigo: a.codigo,
  prefijo: a.codigo,
}));
